import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal, Spin } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useEvents } from '../../common/EventContext';
import { setFormValidationErrors } from '../../common/Utils';
import { useBusinessContext } from '../../context/BusinessContext';
import {
  PettyCash,
  PettyCashItem,
  PettyCashItemStatus,
  PettyCashStatus,
} from '../../dto/PettyCash';
import { OptionedInputValue } from '../../common/OptionedInput';
import { useConnection } from '../../connection/connection';

export type PettyCashItemEditModalInstance = {
  create: (pettyCashId: number) => void;
  edit: (pettyCashId: number, pettyCashItemId: number) => void;
};

type Props = {
  user: boolean;
};

const PettyCashItemEditModal: ForwardRefRenderFunction<
  PettyCashItemEditModalInstance,
  Props
> = (props, ref) => {
  const [title, setTitle] = useState<string>('Create Petty Cash Account');
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCurrencyAmount, setSelectedCurrencyAmount] = useState<
    OptionedInputValue | undefined
  >(undefined);
  const [form] = Form.useForm<PettyCashItem>();
  const events = useEvents();
  const connection = useConnection();
  const business = useBusinessContext();
  const [existingPettyCash, setExistingPettyCash] = useState<PettyCash>();
  const [existingPettyCashItem, setExistingPettyCashItem] =
    useState<PettyCashItem>();

  useImperativeHandle(
    ref,
    () => ({
      create: (pettyCashId: number) => {
        setTitle('Create Petty Cash Item');
        setVisible(true);
        setSaving(false);
        setLoading(true);
        setExistingPettyCashItem(undefined);
        form.resetFields();
        connection
          .get<PettyCash>(`${business.businessId}/petty-cash/${pettyCashId}`)
          .then((res) => {
            setExistingPettyCash(res.data);
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            Modal.error({ title: 'Error', content: 'Something went wrong!' });
            setVisible(false);
          });
      },
      edit: (pettyCashId: number, pettyCashItemId: number) => {
        setTitle('Edit Petty Cash Item');
        setVisible(true);
        setSaving(false);
        setLoading(true);
        form.resetFields();
        form.setFieldsValue({});
        connection
          .get<PettyCashItem>(
            `${business.businessId}/${pettyCashId}/petty-cash-item/${pettyCashItemId}`,
          )
          .then((res) => {
            console.log(res.data);
            form.setFieldsValue(res.data);
            setExistingPettyCashItem(res.data);
            setExistingPettyCash({
              pettyCashId: res.data.pettyCashId,
              businessId: res.data.businessId,
              siteId: res.data.siteId,
            } as PettyCash);
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            Modal.error({ title: 'Error', content: 'Something went wrong!' });
            setVisible(false);
          });
      },
    }),
    [business.businessId, connection, form],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'name',
        type: 'string',
        name: 'Name',
        props: { placeholder: 'Name' },
        options: {
          rules: [{ required: true, message: 'Name is required' }],
        },
      },
      {
        id: 'amount',
        type: 'price',
        name: 'Amount',
        layout: { xs: 12 },
        options: {
          rules: [{ required: true, message: 'Amount is required' }],
        },
      },
      {
        id: 'category',
        type: 'select2',
        name: 'Category',
        layout: { xs: 12 },
        props: {
          showSearch: true,
          optionFilterProp: 'label',
          options: business.select['expenseTypes'],
        },
        options: {
          rules: [{ required: true, message: 'Category is required' }],
        },
      },
      {
        id: 'vatType',
        type: 'select2',
        name: 'Vat',
        layout: { xs: 12 },
        props: {
          options: [
            { label: 'Apply VAT', value: 'Apply VAT' },
            { label: 'Apply SVAT', value: 'Apply SVAT' },
            { label: 'No VAT', value: 'No VAT' },
          ],
        },
        options: {
          rules: [{ required: true, message: 'Vat is required' }],
        },
      },
      {
        id: 'vatAmount',
        type: 'number',
        name: 'Vat Amount',
        layout: { xs: 12 },
        visible: (v) => v.vatType && v.vatType !== 'No VAT',
        props: {
          placeholder: 'Vat amount',
          min: 0,
        },
        options: {
          rules: [{ required: true, message: 'Vat amount is required' }],
        },
      },
      {
        id: 'refNo',
        type: 'string',
        name: 'Ref No',
        props: { placeholder: 'Ref No' },
      },
      {
        id: 'payee',
        type: 'string',
        name: 'Payee',
        props: { placeholder: 'Payee' },
      },
      {
        id: 'voucher',
        type: 'string',
        name: 'Voucher',
        props: { placeholder: 'Voucher' },
      },
      {
        id: 'chequeNo',
        type: 'string',
        name: 'Cheque No',
        props: { placeholder: 'Cheque No' },
      },
      {
        id: 'notes',
        type: 'text',
        name: 'Notes',
      },
      // {
      //   id: 'receipt',
      //   type: 'upload',
      //   name: 'Receipt',
      // },
    ];
    // if (existingPettyCashItem) {
    //   formSchema.push({
    //     id: 'status',
    //     type: 'select',
    //     name: 'Status',
    //     props: { placeholder: 'Status', options: ['Active', 'Inactive'] },
    //     options: {
    //       rules: [{ required: true, message: 'Status is required' }],
    //     },
    //   });
    // }
    return formSchema;
  }, [business.select]);

  const submit = useCallback(
    (values: PettyCashItem) => {
      setSaving(true);
      if (existingPettyCashItem && existingPettyCash) {
        connection
          .patch<PettyCashItem>(
            `${business.businessId}/${existingPettyCash.pettyCashId}/petty-cash-item/${existingPettyCashItem.pettyCashItemId}`,
            values,
          )
          .then((res) => {
            events.emit('PETTYCASHITEM', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .post<PettyCashItem>(
            `${business.businessId}/${existingPettyCash?.pettyCashId}/petty-cash-item`,
            values,
          )
          .then((res) => {
            events.emit('PETTYCASHITEM', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      }
    },
    [
      business.businessId,
      connection,
      events,
      existingPettyCash,
      existingPettyCashItem,
      form,
    ],
  );

  const statusChange = useCallback(
    (value: string) => {
      setSaving(true);
      if (existingPettyCashItem && existingPettyCash) {
        connection
          .patch<PettyCashItem>(
            `${business.businessId}/${existingPettyCash.pettyCashId}/petty-cash-item/${existingPettyCashItem.pettyCashItemId}/status`,
            { status: value },
          )
          .then((res: any) => {
            events.emit('PETTYCASHITEM', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .catch((err: any) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .catch((err: any) => {
            if (!setFormValidationErrors(form, err)) {
              Modal.error({
                title: 'Error',
                content: 'Something went wrong. Please try again later.',
              });
            }
          })
          .finally(() => setSaving(false));
      }
    },
    [
      business.businessId,
      connection,
      events,
      existingPettyCash,
      existingPettyCashItem,
      form,
    ],
  );

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={() => setVisible(false)}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={() => setVisible(false)}>Cancel</Button>
            {existingPettyCashItem && !props.user && (
              <>
                {existingPettyCashItem.status !==
                  PettyCashItemStatus.Declined && (
                  <Button
                    type="primary"
                    style={{ backgroundColor: 'red', color: 'white' }}
                    loading={saving}
                    onClick={() => {
                      statusChange(PettyCashItemStatus.Declined);
                    }}
                  >
                    Declined
                  </Button>
                )}
                {existingPettyCashItem.status !==
                  PettyCashItemStatus.Approved && (
                  <Button
                    type="primary"
                    style={{ backgroundColor: 'green', color: 'white' }}
                    loading={saving}
                    onClick={() => {
                      statusChange(PettyCashItemStatus.Approved);
                    }}
                  >
                    Approved
                  </Button>
                )}
              </>
            )}
            <Button type="primary" loading={saving} onClick={form.submit}>
              OK
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Spin spinning={loading}>
        <Form onFinish={submit} form={form} layout="vertical">
          <Form.Items schema={schema} />
        </Form>
      </Spin>
    </Drawer>
  );
};

export const usePettyCashItemEditModal = () =>
  useRef<PettyCashItemEditModalInstance>(null);

export default React.forwardRef<PettyCashItemEditModalInstance, Props>(
  PettyCashItemEditModal,
);
